import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Building2 } from "lucide-react";
import { LeadBadge, LEAD_STYLE, fmtDateTime } from "@/components/bp/bpLabels";

const BP_STEPS = ["new", "contacted", "qualified", "demo"];

/** Fiche d'un lead côté Business Partner : statut (hors Gagné/Perdu), notes, timeline. */
export const LeadDrawer = ({ leadId, onClose, onChanged }) => {
  const [lead, setLead] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => leadId && api.get(`/bp/leads/${leadId}`).then((r) => setLead(r.data)).catch(() => { toast.error("Lead introuvable"); onClose(); });
  useEffect(() => { setLead(null); load(); /* eslint-disable-next-line */ }, [leadId]);

  const patch = async (body, ok) => {
    setBusy(true);
    try { await api.patch(`/bp/leads/${lead.id}`, body); toast.success(ok); await load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const decided = lead && ["won", "lost"].includes(lead.status);

  return (
    <Sheet open={!!leadId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="lead-drawer">
        {!lead ? <><SheetTitle className="sr-only">Chargement</SheetTitle><SheetDescription className="sr-only">Détail du lead</SheetDescription><div className="text-xs text-[#868E96] p-4">Chargement…</div></> : (
          <>
            <SheetHeader>
              <div className="flex items-center gap-2"><span className="text-[10px] font-mono text-[#868E96]" data-testid="lead-reference">{lead.reference}</span><LeadBadge status={lead.status} testid="lead-status" /></div>
              <SheetTitle className="font-display text-2xl font-black tracking-tight" data-testid="lead-company-name">{lead.company_name}</SheetTitle>
              <SheetDescription className="text-xs">{lead.contact_prenom} {lead.contact_nom} · enregistré le {fmtDateTime(lead.created_at)}</SheetDescription>
            </SheetHeader>
            <div className="mt-4 grid sm:grid-cols-2 gap-2 text-xs">
              <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1.5 hover:text-[#002FA7]"><Mail className="h-3.5 w-3.5" />{lead.email}</a>
              {lead.telephone && <a href={`tel:${lead.telephone}`} className="inline-flex items-center gap-1.5 hover:text-[#002FA7]"><Phone className="h-3.5 w-3.5" />{lead.telephone}</a>}
              {(lead.ville || lead.secteur) && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{[lead.ville, lead.secteur].filter(Boolean).join(" · ")}</span>}
              {lead.siret && <span className="inline-flex items-center gap-1.5 font-mono"><Building2 className="h-3.5 w-3.5" />{lead.siret}</span>}
            </div>
            {decided ? (
              <div className={`mt-4 p-3 text-xs border-2 ${lead.status === "won" ? "border-[#059669] bg-[#ECFDF5]" : "border-[#868E96] bg-[#F8F9FA]"}`} data-testid="lead-decided">
                <b>Tranché par PAG : {LEAD_STYLE[lead.status].label}</b>{lead.decision_message ? ` — ${lead.decision_message}` : ""}
              </div>
            ) : (
              <section className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold mb-2">Avancement</div>
                <div className="flex flex-wrap gap-1.5">
                  {BP_STEPS.map((st) => (
                    <button key={st} type="button" disabled={busy} onClick={() => patch({ status: st }, `Statut : ${LEAD_STYLE[st].label}`)} data-testid={`lead-set-${st}`}
                            className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 border transition ${lead.status === st ? LEAD_STYLE[st].cls + " border-transparent" : "bg-white border-[#DEE2E6] hover:border-[#0A0A0A]"}`}>{LEAD_STYLE[st].label}</button>
                  ))}
                </div>
                <div className="text-[10px] text-[#868E96] mt-1.5">« Gagné » et « Perdu » sont décidés par l'équipe PAG après le premier paiement du client.</div>
              </section>
            )}
            <section className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold mb-2">Notes</div>
              <div className="text-xs whitespace-pre-wrap bg-[#FFFBEB] border border-[#DEE2E6] p-2 min-h-[40px]" data-testid="lead-notes-view">{lead.notes || "—"}</div>
              {!decided && (
                <div className="mt-2 flex gap-2">
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Ajouter une entrée au journal…" className="text-xs" data-testid="lead-note-input" />
                  <Button size="sm" onClick={async () => { await patch({ note }, "Note ajoutée"); setNote(""); }} disabled={!note.trim() || busy} className="h-auto bg-[#0A0A0A] text-white" data-testid="lead-note-add">Ajouter</Button>
                </div>
              )}
            </section>
            <section className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold mb-2">Journal</div>
              <ol className="border-l-2 border-[#DEE2E6] pl-3 space-y-2" data-testid="lead-timeline">
                {[...(lead.timeline || [])].reverse().map((t) => <li key={t.id} className="text-xs"><span className="text-[10px] text-[#868E96] font-mono">{fmtDateTime(t.at)} · {t.actor}</span><div>{t.label}</div></li>)}
              </ol>
            </section>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
